<script setup lang="ts">
import PortableEditor from '@pkg/portable-text-editor/web/PortableEditor.vue';
import { BagHeader } from '@pkg/ui/components/ui/bag-header';
import { Breadcrumbs } from '@pkg/ui/components/ui/breadcrumbs';
import BagWithFoodIcon from '@pkg/cms-icons/icons/Bag-with-food_Icon.svg';
import Co2Icon from '@pkg/cms-icons/icons/CO2_Icon.svg';
import type { SurpriseBag } from '@pkg/types/surpriseBag';
import { brandProfilePageUrl, resolvePartnerSlugSegment } from '../utils/brandProfileUrls';
import { getBrandListHeroDescription, getCityHeroDescription } from '../utils/getBrandDescription';
import type { BrandProfileSanityDoc, BrandProfileMarketingDoc } from '~/app/types/brandProfiles';

const props = defineProps<{
  content?: {
    _type: string;
    title?: string;
    seoSocial?: unknown;
    brandProfileSanity?: BrandProfileSanityDoc | null;
    brandProfileMarketing?: BrandProfileMarketingDoc | null;
    brandProfileSurpriseBagItems?: SurpriseBag[];
  } | null;
  pageTitle: string;
  selectedCity: string;
  showCitiesList: boolean;
}>();
const brandName = computed(() => props.content?.brandProfileSanity?.name?.trim() ||
    props.content?.title?.trim() || 'Partner');

const { locale } = useI18n();

const marketingCoverImage = computed(
  () => props.content?.brandProfileMarketing?.item?.item?.coverPicture?.currentUrl || '',
);
const marketingBrandLogo = computed(
  () => props.content?.brandProfileMarketing?.item?.item?.logoPicture?.currentUrl || '',
);

const coverImage = computed(() => props.content?.brandProfileSanity?.heroImage?.url || marketingCoverImage.value);
const brandLogo = computed(() => props.content?.brandProfileSanity?.logo?.url || marketingBrandLogo.value);
const brandRating = computed(() => props.content?.brandProfileMarketing?.chainRating || 0);

const showRatings = computed(() => props.content?.brandProfileSanity?.showRatings !== false);
const showSavedCo2 = computed(() => props.content?.brandProfileSanity?.showSavedCo2 !== false);
const showMealsSaved = computed(() => props.content?.brandProfileSanity?.showMealsSaved !== false);

const bagHeaderProps = computed(() => ({
  image: coverImage.value,
  logo: brandLogo.value,
  altText: props.content?.brandProfileSanity?.heroImage?.alt || `${brandName.value} - Too Good To Go`,
  logoAltText: props.content?.brandProfileSanity?.logo?.alt || `${brandName.value} logo`,
  availableBag: true,
  ratings: props.showCitiesList && showRatings.value ? brandRating.value : undefined,
}));

const description = computed(() => {
  if (props.showCitiesList) {
    return getBrandListHeroDescription({
      brandProfileSanity: props.content?.brandProfileSanity,
      brandName: brandName.value,
    });
  } else {
    return getCityHeroDescription({
      brandProfileSanity: props.content?.brandProfileSanity,
      brandName: brandName.value,
      cityName: props.selectedCity,
    });
  }
});
const hasDescription = computed(() => Array.isArray(description.value) && description.value.length > 0);
const co2EquivalentKg = computed(() => props.content?.brandProfileMarketing?.co2EquivalentKg?.toLocaleString(locale.value) || 0);
const mealsSaved = computed(() => props.content?.brandProfileMarketing?.mealsSaved?.toLocaleString(locale.value) || 0);

const defaultTexts = computed(() => props.content?.brandProfileSanity?.brandProfileDefaultTexts);
const savedCo2Label = computed(() => defaultTexts.value?.savedCo2Label ? `${defaultTexts.value?.savedCo2Label?.trim()}: ` : 'Saved CO2 in kg: ');
const mealsSavedLabel = computed(() => defaultTexts.value?.mealsSavedLabel ? `${defaultTexts.value?.mealsSavedLabel?.trim()}: ` : 'Meals saved: ');

const route = useRoute();

const partnerPathSegment = computed(() =>
  resolvePartnerSlugSegment({
    profileSlug: props.content?.brandProfileSanity?.profileSlug,
    path: route.path,
    brandNameFallback: brandName.value,
  }),
);

const pageUrl = computed(() => brandProfilePageUrl(locale.value, partnerPathSegment.value));
const { t } = useI18n();
const localePath = useLocalePath();
const homeLink = computed(() => localePath('/'));

const breadcrumbs = computed(() => {
  if (props.showCitiesList) {
    return [
      { name: t('home'), path: homeLink.value, isLast: false }, // PTE Home name translated
      { name: brandName.value, path: '', isLast: true },
    ];
  } else {
    return [
      { name: t('home'), path: homeLink.value, isLast: false }, // PTE Home name translated
      { name: brandName.value, path: pageUrl.value, isLast: false },
      { name: props.selectedCity, path: '', isLast: true },
    ];
  }
});

const emit = defineEmits<{
  'change-to-list': [value: boolean];
}>();

const handleBreadcrumbClick = () => {
  emit('change-to-list', true);
};
</script>

<template>
  <Breadcrumbs class="flex justify-start" :breadcrumbs="breadcrumbs" @on-click="handleBreadcrumbClick" />
  <section class="s-brand__hero__header">
    <BagHeader v-bind="bagHeaderProps" />
  </section>

  <section>
    <div class="s-brand__title__wrapper">
      <h1 class="s-brand__title">
        {{ pageTitle }}
      </h1>
      <div v-if="hasDescription" class="pt-16 s-brand__hero__description">
        <PortableEditor :content="description" />
      </div>
    </div>
  </section>

  <section v-if="showCitiesList" class="s-brand__hero__sub-hero pt-16">
    <div
      v-if="(showSavedCo2 && co2EquivalentKg) || (showMealsSaved && mealsSaved)"
      class="flex flex-wrap items-center gap-x-16 gap-y-8 mb-16"
    >
      <p v-if="showSavedCo2 && co2EquivalentKg" class="flex items-center gap-12">
        <img
          :src="Co2Icon"
          alt=""
          class="h-24 w-24 shrink-0"
          width="24"
          height="24"
        >
        <span>
          <span class="s-brand__title__tertiary">
            {{ savedCo2Label }}
          </span>
          <span>{{ co2EquivalentKg }}</span>
        </span>
      </p>
      <p v-if="showMealsSaved && mealsSaved" class="flex items-center gap-12">
        <img
          :src="BagWithFoodIcon"
          alt=""
          class="h-24 w-24 shrink-0"
          width="24"
          height="24"
        >
        <span>
          <span class="s-brand__title__tertiary">
            {{ mealsSavedLabel }}
          </span>
          <span>{{ mealsSaved }}</span>
        </span>
      </p>
    </div>
  </section>
</template>

<style scoped>
@import '~/assets/css/mixins/_mixins.index.pcss';

.s-brand__hero {

  &__description {
    width: 100%;
    padding-bottom: theme('spacing.16');
  }

  @screen tablet {
  &__sub-hero {
    display: flex;
    font-size: theme('fontSize.16');

      > div > p:not(:last-child)::after {
        content: '|';
        margin-inline-start: theme('spacing.16');
        color: var(--theme-heading-color-app);
      }
    }
  }

  &__header {
    text-align: center;
    padding: theme('spacing.24') 0px;

    /* `BagHeader` uses `width: 100vw` by default; inside padded pages this can cause horizontal overflow. */
    :deep(.bag-header) {
      width: 100%;
    }

    :deep(.bag-header__image__wrapper) {
      background-color: var(--theme-background-color-app);
      max-width: 100%;
      min-height: 220px;

      @screen tablet-wide {
        height: 280px;
      }
    }
  }
}

.s-brand__title {
  @mixin h3-tenon;
  font-weight: bold;
  color: var(--theme-heading-color-app);

  &__secondary {
    @mixin h5-tenon;
    font-weight: bold;
    padding: theme('spacing.16') 0px;
    color: var(--theme-heading-color-app);
  }
  &__tertiary {
    @mixin body;
    font-weight: bold;
    padding: theme('spacing.16') 0px;
    color: var(--theme-heading-color-app);
  }
}
</style>
